import { FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useCreatePersonEvent } from '../hooks/useChurchAdmin'
import { geocodeAddress } from '../lib/geocode'
import { computeRsvpClosesAt, type RsvpCloseOption } from '../lib/rsvpClose'
import { isPastDateTime } from '../lib/dates'
import { EventTimePicker } from '../components/ui/DateTimePicker'
import { ApiError } from '../lib/api'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Icons } from '../components/ui/Icons'
import { Button } from '../components/ui/Button'
import LocationPicker from '../components/LocationPicker'
import { useCurrentPosition } from '../hooks/useCurrentPosition'
import { WizardModeToggle } from '../components/event/WizardModeToggle'
import { WizardStepRail } from '../components/event/WizardStepRail'
import { VisibilityPicker } from '../components/event/VisibilityPicker'
import { SectionCard } from '../components/event/EventFormPrimitives'
import { BasicsFields } from '../components/event/BasicsFields'
import { RsvpSettingsFields } from '../components/event/RsvpSettingsFields'
import { MoreDetailsFields } from '../components/event/MoreDetailsFields'
import { MissingFieldsNotice } from '../components/event/MissingFieldsNotice'
import type { EventType, EventAttributes, DishGoalCategory } from '@fellowship/shared'

// ── Wizard steps ──────────────────────────────────────────────────────────────

const WIZARD_STEPS = [
  { label: 'The basics', sublabel: 'Type, title, description' },
  { label: 'When & where', sublabel: 'Date, time, location' },
  { label: 'Privacy & RSVP', sublabel: 'Who sees it, approvals' },
  { label: 'More details', sublabel: 'Optional extras' },
  { label: 'Review & publish', sublabel: 'One last look' },
]

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CreatePersonEventPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const currentPosition = useCurrentPosition()
  const createEvent = useCreatePersonEvent()

  // ── Mode + step ──
  const [mode, setMode] = useState<'guided' | 'form'>(() => {
    try { return (localStorage.getItem('eventFormMode') as 'guided' | 'form') ?? 'guided' }
    catch { return 'guided' }
  })
  const [step, setStep] = useState(0)

  function handleModeChange(m: 'guided' | 'form') {
    setMode(m)
    if (m === 'guided') setStep(0)
    try { localStorage.setItem('eventFormMode', m) } catch { /* ignore */ }
  }

  // ── Event data ──
  const [eventType, setEventType] = useState<EventType>('potluck')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [locationName, setLocationName] = useState('')
  const [addressText, setAddressText] = useState('')
  const [locationLat, setLocationLat] = useState<number | null>(null)
  const [locationLng, setLocationLng] = useState<number | null>(null)
  const [locationMode, setLocationMode] = useState<'private' | 'venue' | 'pin'>('private')
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'invite_only'>('public')
  const [audience, setAudience] = useState<'everyone' | 'denomination'>('everyone')
  const [attributes, setAttributes] = useState<EventAttributes>({ bringADish: true })
  const [maxAttendees, setMaxAttendees] = useState('')
  const [rsvpRequiresApproval, setRsvpRequiresApproval] = useState(true)
  const [hideAddressUntilApproved, setHideAddressUntilApproved] = useState(true)
  const [rsvpClose, setRsvpClose] = useState<RsvpCloseOption>('none')

  // Dish goals
  const [dishCategories, setDishCategories] = useState<DishGoalCategory[]>([])

  // Outdoor
  const [outdoorDifficulty, setOutdoorDifficulty] = useState<'easy' | 'moderate' | 'strenuous'>('easy')
  const [outdoorDistance, setOutdoorDistance] = useState('')
  const [outdoorElevation, setOutdoorElevation] = useState('')
  const [outdoorMeetupNote, setOutdoorMeetupNote] = useState('')

  // Study
  const [studyTopic, setStudyTopic] = useState('')
  const [studyScripture, setStudyScripture] = useState('')

  // UI state
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [geocodeError, setGeocodeError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Form-mode "More details" panel open state — independent of the dish/outdoor
  // toggles inside it, so toggling those off no longer collapses the whole panel.
  // Defaults open since the default type (potluck) has suggested content.
  const [extrasOpen, setExtrasOpen] = useState(true)

  // ── Handlers ──

  function handleTypeChange(key: EventType) {
    setEventType(key)
    setAttributes((prev) => ({
      ...prev,
      bringADish: key === 'potluck' ? true : undefined,
      outdoorActivity: key === 'outdoors' ? true : undefined,
    }))
    // Reveal the details panel when the chosen type has suggested content.
    if (key === 'potluck' || key === 'outdoors' || key === 'study') setExtrasOpen(true)
  }

  function validateStep(s: number): boolean {
    const errs: Record<string, string> = {}
    if (s === 0) {
      if (!title.trim()) errs.title = 'Title is required'
    }
    if (s === 1) {
      if (!startsAt) errs.startsAt = 'Start date and time is required'
      else if (isPastDateTime(startsAt)) errs.startsAt = "Start time can't be in the past"
      const hasPinCoords = locationMode === 'pin' && locationLat !== null && locationLng !== null
      if (!addressText.trim() && !hasPinCoords) errs.addressText = 'Address is required'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!title.trim()) errs.title = 'Title is required'
    if (!startsAt) errs.startsAt = 'Start date and time is required'
    else if (isPastDateTime(startsAt)) errs.startsAt = "Start time can't be in the past"
    const hasPinCoords = locationMode === 'pin' && locationLat !== null && locationLng !== null
    if (!addressText.trim() && !hasPinCoords) errs.addressText = 'Address is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleContinue() {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, 4))
  }

  function buildMetadata() {
    const meta: Record<string, unknown> = {}
    if (attributes.bringADish && dishCategories.length > 0) {
      meta.dishGoals = { categories: dishCategories }
    }
    if (attributes.outdoorActivity) {
      meta.difficulty = outdoorDifficulty
      if (outdoorDistance) meta.distanceMiles = parseFloat(outdoorDistance)
      if (outdoorElevation) meta.elevationFt = parseInt(outdoorElevation)
      if (outdoorMeetupNote.trim()) meta.meetupNote = outdoorMeetupNote.trim()
    }
    if (eventType === 'study') {
      if (studyTopic.trim()) meta.topic = studyTopic.trim()
      if (studyScripture.trim()) meta.scriptureRef = studyScripture.trim()
    }
    return Object.keys(meta).length > 0 ? meta : undefined
  }

  async function resolveCoords() {
    if (locationLat !== null && locationLng !== null) return { lat: locationLat, lng: locationLng }
    return geocodeAddress(addressText.trim())
  }

  async function submitEvent(status: 'active' | 'draft') {
    if (!validate()) return
    setGeocodeError('')
    setIsSubmitting(true)
    const coords = await resolveCoords()
    if (!coords) {
      setGeocodeError('Could not locate that address. Use the "Locate →" button or drag the map pin.')
      setIsSubmitting(false)
      return
    }
    createEvent.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        eventType,
        locationName: locationName.trim() || undefined,
        addressText: addressText.trim(),
        locationLat: coords.lat,
        locationLng: coords.lng,
        locationMode,
        visibility,
        audience,
        rsvpRequiresApproval,
        // Hiding the address is only meaningful for a private home; venue/pin are public.
        hideAddressUntilApproved: locationMode === 'private' && hideAddressUntilApproved,
        maxAttendees: maxAttendees ? parseInt(maxAttendees) : undefined,
        rsvpClosesAt: computeRsvpClosesAt(rsvpClose, startsAt),
        isRecurring: false,
        tags: [],
        imageUrl: imageUrl ?? undefined,
        attributes,
        metadata: buildMetadata(),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
        status,
      },
      {
        onSuccess: ({ data }) => {
          if (data.event.id) navigate(`/events/${data.event.id}`, { state: { from: 'my-events' } })
          else navigate('/app')
        },
        onSettled: () => setIsSubmitting(false),
      },
    )
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Guard against implicit submits (e.g. pressing Enter in a field) before the
    // final wizard step — advance instead of publishing, so Review is never skipped.
    if (mode === 'guided' && step < 4) {
      handleContinue()
      return
    }
    await submitEvent('active')
  }

  async function handleSaveDraft() {
    await submitEvent('draft')
  }

  const serverError = createEvent.error instanceof ApiError ? createEvent.error.message : null
  const busy = isSubmitting || createEvent.isPending

  // ─────────────────────────────────────────────────────────────────────────────
  // Shared field groups (called as functions, not JSX components, to avoid remount)
  // ─────────────────────────────────────────────────────────────────────────────

  function WhenFields() {
    return (
      <EventTimePicker
        startsAt={startsAt}
        endsAt={endsAt}
        disablePast
        onStartsAtChange={setStartsAt}
        onEndsAtChange={setEndsAt}
        startsAtError={errors.startsAt}
      />
    )
  }

  function WhereFields() {
    return (
      <LocationPicker
        addressText={addressText}
        onAddressChange={setAddressText}
        lat={locationLat}
        lng={locationLng}
        onCoordinatesChange={(lat, lng) => { setLocationLat(lat); setLocationLng(lng) }}
        addressError={errors.addressText}
        showModeToggle
        mode={locationMode}
        onModeChange={setLocationMode}
        onHideAddressChange={setHideAddressUntilApproved}
        locationName={locationName}
        onLocationNameChange={setLocationName}
        defaultLat={currentPosition?.lat ?? user?.locationLat}
        defaultLng={currentPosition?.lng ?? user?.locationLng}
      />
    )
  }

  function PrivacyRsvpFields() {
    return (
      <div className="flex flex-col gap-4">
        <VisibilityPicker
          visibility={visibility}
          audience={audience}
          denomination={user?.denomination}
          onChange={({ visibility: v, audience: a }) => { setVisibility(v); setAudience(a) }}
        />
        <RsvpSettingsFields
          maxAttendees={maxAttendees}
          onMaxAttendeesChange={setMaxAttendees}
          rsvpClose={rsvpClose}
          onRsvpCloseChange={setRsvpClose}
          requiresApproval={rsvpRequiresApproval}
          onRequiresApprovalChange={setRsvpRequiresApproval}
          onHideAddressChange={setHideAddressUntilApproved}
          locationMode={locationMode}
        />
      </div>
    )
  }

  function MoreDetailsContent() {
    return (
      <MoreDetailsFields
        eventType={eventType}
        attributes={attributes}
        setAttributes={setAttributes}
        dishCategories={dishCategories} setDishCategories={setDishCategories}
        outdoorDifficulty={outdoorDifficulty} setOutdoorDifficulty={setOutdoorDifficulty}
        outdoorDistance={outdoorDistance} setOutdoorDistance={setOutdoorDistance}
        outdoorElevation={outdoorElevation} setOutdoorElevation={setOutdoorElevation}
        outdoorMeetupNote={outdoorMeetupNote} setOutdoorMeetupNote={setOutdoorMeetupNote}
        studyTopic={studyTopic} setStudyTopic={setStudyTopic}
        studyScripture={studyScripture} setStudyScripture={setStudyScripture}
        imageUrl={imageUrl} setImageUrl={setImageUrl}
      />
    )
  }

  function ReviewContent() {
    const formatDt = (dt: string) => {
      if (!dt) return '—'
      return new Date(dt).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    }
    const rows: { label: string; value: string }[] = [
      { label: 'Type', value: eventType.charAt(0).toUpperCase() + eventType.slice(1) },
      { label: 'Title', value: title || '—' },
      { label: 'Starts', value: formatDt(startsAt) },
      { label: 'Location', value: locationMode === 'private' ? 'Private home' : locationMode === 'venue' ? 'Public venue' : 'Pinned location' },
      ...(locationName.trim() ? [{ label: 'Venue', value: locationName.trim() }] : []),
      { label: 'Address', value: addressText || '—' },
      { label: 'Visibility', value:
        visibility === 'public' && audience === 'denomination' && user?.denomination
          ? `${user.denomination.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} only`
          : visibility === 'invite_only' ? 'Invite only' : visibility.charAt(0).toUpperCase() + visibility.slice(1) },
      { label: 'RSVP approval', value: rsvpRequiresApproval ? 'Required' : 'Not required' },
    ]
    if (attributes.bringADish) rows.push({ label: 'Dish coordination', value: 'On' })
    if (attributes.outdoorActivity) rows.push({ label: 'Outdoor details', value: 'On' })
    return (
      <div className="flex flex-col gap-3">
        <div className="text-[20px] font-bold leading-snug mb-1">Ready to publish?</div>
        <div className="text-[13.5px] text-muted mb-2">Check your details — you can edit after publishing.</div>
        <div className="rounded-xl border border-line-soft overflow-hidden">
          {rows.map(({ label, value }, i) => (
            <div key={label} className={`flex gap-4 px-4 py-2.5 ${i % 2 === 0 ? 'bg-s2' : 'bg-surface'}`}>
              <div className="text-[12.5px] font-semibold text-muted w-28 shrink-0">{label}</div>
              <div className="text-[13px] text-ink">{value}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Error banner (shared)
  // ─────────────────────────────────────────────────────────────────────────────

  const ErrorBanner = (serverError || geocodeError) ? (
    <div role="alert" className="px-4 py-3 rounded-xl bg-danger-soft border border-danger-line text-danger-deep text-[13px]">
      {serverError ?? geocodeError}
    </div>
  ) : null

  // ─────────────────────────────────────────────────────────────────────────────
  // Header (shared between modes)
  // ─────────────────────────────────────────────────────────────────────────────

  const PageHeader = (
    <div className="flex items-start justify-between mb-6 gap-4">
      <div>
        <div className="eyebrow mb-1">Create</div>
        <h1 className="serif m-0 text-[32px] sm:text-[38px] tracking-[-0.02em]">
          Host an{' '}
          <span className="serif-i text-primary">event</span>
        </h1>
      </div>
      <WizardModeToggle mode={mode} onChange={handleModeChange} />
    </div>
  )

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <PublicLayout>
      <div
        className={`px-4 sm:px-6 pt-10 pb-20 overflow-x-hidden w-full ${mode === 'form' ? 'mx-auto' : ''}`}
        style={mode === 'form' ? { maxWidth: 600 } : undefined}
      >
        {PageHeader}

        {mode === 'form' ? (
          // ── Just the form ────────────────────────────────────────────────────
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            {ErrorBanner}

            <SectionCard>
              <BasicsFields
                eventType={eventType}
                onTypeChange={handleTypeChange}
                title={title}
                onTitleChange={setTitle}
                titleError={errors.title}
                description={description}
                onDescriptionChange={setDescription}
              />
            </SectionCard>

            <SectionCard title="When">
              {WhenFields()}
            </SectionCard>

            <SectionCard title="Where">
              {WhereFields()}
            </SectionCard>

            <SectionCard title="Privacy & RSVP">
              {PrivacyRsvpFields()}
            </SectionCard>

            {/* More details — collapsible; toggle is independent of the modules inside */}
            <div className="bg-surface border border-line-soft rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setExtrasOpen((o) => !o)}
                aria-expanded={extrasOpen}
                className="w-full flex items-center gap-2 px-4 sm:px-6 py-4 bg-transparent border-0 cursor-pointer font-sans text-left"
              >
                <Icons.chevron size={14} className={`text-muted transition-transform ${extrasOpen ? '' : '-rotate-90'}`} />
                <span className="text-[13px] font-bold text-ink-2 tracking-[0.04em] uppercase">More details</span>
                {!extrasOpen && (
                  <span className="text-[12px] text-muted font-normal normal-case tracking-normal ml-1">
                    photo, dish goals, attributes…
                  </span>
                )}
              </button>
              {extrasOpen && (
                <div className="px-4 sm:px-6 pb-[22px]">
                  {MoreDetailsContent()}
                </div>
              )}
            </div>

            <MissingFieldsNotice errors={errors} />

            {/* Footer */}
            <div className="flex gap-3 pt-2">
              <Button variant="ghost" type="button" onClick={handleSaveDraft} disabled={busy}>
                Save draft
              </Button>
              <Button variant="primary" type="submit" disabled={busy} className="flex-1">
                {busy ? 'Publishing…' : 'Publish event'}
              </Button>
            </div>
          </form>
        ) : (
          // ── Guided wizard ────────────────────────────────────────────────────
          <form onSubmit={handleSubmit} noValidate>
            {ErrorBanner && <div className="mb-4">{ErrorBanner}</div>}

            <div className="flex gap-6 items-start">
              <WizardStepRail
                currentStep={step}
                steps={WIZARD_STEPS}
                onStepClick={setStep}
              />

              <div
                className="flex-1 min-w-0 bg-surface border border-line-soft rounded-[20px] flex flex-col"
                style={{ minHeight: 430, boxShadow: '0 2px 8px rgba(31,26,46,.06)' }}
              >
                {/* Step content */}
                <div className="flex-1 p-6 sm:p-7">
                  {step === 0 && (
                    <div className="flex flex-col gap-4">
                      <div className="text-[20px] font-bold mb-1">What kind of gathering?</div>
                      <BasicsFields
                        eventType={eventType}
                        onTypeChange={handleTypeChange}
                        title={title}
                        onTitleChange={setTitle}
                        titleError={errors.title}
                        description={description}
                        onDescriptionChange={setDescription}
                      />
                    </div>
                  )}

                  {step === 1 && (
                    <div className="flex flex-col gap-5">
                      <div className="text-[20px] font-bold mb-1">When & where</div>
                      <div>
                        <div className="text-[12px] font-bold text-ink-2 uppercase tracking-[0.04em] mb-3">When</div>
                        {WhenFields()}
                      </div>
                      <div>
                        <div className="text-[12px] font-bold text-ink-2 uppercase tracking-[0.04em] mb-3">Where</div>
                        {WhereFields()}
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="flex flex-col gap-4">
                      <div className="text-[20px] font-bold mb-1">Privacy & RSVP</div>
                      {PrivacyRsvpFields()}
                    </div>
                  )}

                  {step === 3 && (
                    <div>
                      <div className="text-[20px] font-bold mb-1">Anything to add?</div>
                      <div className="text-[13.5px] text-muted mb-4">All optional — turn on what applies.</div>
                      {MoreDetailsContent()}
                    </div>
                  )}

                  {step === 4 && ReviewContent()}

                  {step === 4 && Object.keys(errors).length > 0 && (
                    <div className="mt-4">
                      <MissingFieldsNotice errors={errors} />
                    </div>
                  )}
                </div>

                {/* Wizard nav */}
                <div className="border-t border-line-soft px-6 sm:px-7 py-4 flex items-center justify-between gap-3">
                  <div>
                    {step > 0 && (
                      <Button variant="bare" type="button" onClick={() => setStep((s) => s - 1)}>
                        <Icons.back size={14} />
                        Back
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    {step === 4 && (
                      <Button variant="ghost" type="button" onClick={handleSaveDraft} disabled={busy}>
                        Save draft
                      </Button>
                    )}
                    {step < 4 ? (
                      <Button variant="primary" type="button" onClick={handleContinue}>
                        Continue
                      </Button>
                    ) : (
                      <Button variant="primary" type="submit" disabled={busy}>
                        {busy ? 'Publishing…' : 'Publish event'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </PublicLayout>
  )
}
